<template>
    <v-dialog
        v-model="show"
        max-width="385"
        persistent
        content-class="feedback-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        @click:outside="handleClose"
    >
        <v-card>
            <v-card-title id="feedback-modal-title" class="modal-header">
                Was this result helpful?
                <v-spacer></v-spacer>
                <v-btn
                    icon
                    small
                    aria-label="Close feedback dialog"
                    data-test="feedback-modal-close-btn"
                    @click="handleClose"
                >
                    <i class="fas fa-times"></i>
                </v-btn>
            </v-card-title>
            <v-card-text class="modal-content">
                <div class="feedback-icons">
                    <button
                        type="button"
                        class="feedback-btn"
                        :class="{ active: localFeedback === 'positive' }"
                        data-test="feedback-modal-thumbs-up-btn"
                        aria-label="Thumbs up"
                        @click="localFeedback = 'positive'"
                    >
                        <i :class="localFeedback === 'positive' ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'"></i>
                        <span>Yes</span>
                    </button>
                    <button
                        type="button"
                        class="feedback-btn"
                        :class="{ active: localFeedback === 'negative' }"
                        data-test="feedback-modal-thumbs-down-btn"
                        aria-label="Thumbs down"
                        @click="localFeedback = 'negative'"
                    >
                        <i :class="localFeedback === 'negative' ? 'fas fa-thumbs-down' : 'far fa-thumbs-down'"></i>
                        <span>No</span>
                    </button>
                </div>
                <div class="feedback-input-section">
                    <label for="feedback-text" class="feedback-label">
                        Tell us more (optional)
                    </label>
                    <v-textarea
                        id="feedback-text"
                        v-model="feedbackText"
                        outlined
                        dense
                        rows="3"
                        placeholder="What could be improved?"
                        hide-details
                        class="feedback-textarea"
                        maxlength="500"
                        aria-describedby="feedback-char-count"
                        data-test="feedback-modal-textarea"
                    ></v-textarea>
                    <div id="feedback-char-count" class="character-counter">
                        {{ charactersRemaining }} / 500
                    </div>
                </div>
            </v-card-text>
            <v-card-actions class="modal-actions">
                <v-btn
                    depressed
                    color="primary"
                    aria-label="Submit feedback"
                    data-test="feedback-modal-submit-btn"
                    @click="handleSubmit"
                >
                    Submit
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'FeedbackModal',

    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            localFeedback: 'negative',
            feedbackText: '',
        }
    },

    computed: {
        show: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        charactersRemaining() {
            return 500 - this.feedbackText.length
        },
    },

    watch: {
        value(newVal) {
            if (newVal) {
                this.localFeedback = 'negative'
                this.feedbackText = ''
            }
        },
    },

    methods: {
        handleSubmit() {
            this.$emit('submit', {
                type: this.localFeedback,
                text: this.feedbackText.trim(),
            })
            this.show = false
        },

        handleClose() {
            // User clicked X to cancel - don't submit feedback
            this.$emit('cancel')
            this.show = false
        },
    },
}
</script>

<style lang="scss">
.v-dialog.feedback-modal-dialog {
    position: absolute;
    right: min(11vw, calc(50vw - 192.5px - 20px));
    top: 50%;
    transform: translate(50%, -50%);
    margin: 0;

    // On small screens, center the modal in the viewport
    @media (max-width: 900px) {
        position: fixed;
        right: 50%;
        transform: translate(50%, -50%);
    }
}
</style>

<style lang="scss" scoped>
::v-deep .v-card {
    min-height: 342px;
}

.modal-header {
    font-size: 16px;
    font-weight: 600;
    color: #2b3963;
    padding: 16px 20px;
}

.modal-content {
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
}

.feedback-icons {
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-bottom: 24px;
}

.feedback-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 24px;
    background: none;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: inherit;
    min-width: 80px;

    i {
        font-size: 28px;
        color: #146EB3;
        transition: all 0.2s ease;
    }

    span {
        font-size: 14px;
        font-weight: 500;
        color: #2b3963;
    }

    &:hover {
        i {
            transform: scale(1.1);
        }
    }

    &.active {
        i {
            transform: scale(1.1);
        }
    }
}

.feedback-input-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.feedback-label {
    font-size: 14px;
    font-weight: 500;
    color: #2b3963;
}

.feedback-textarea {
    ::v-deep textarea {
        margin-top: 8px;
    }
}

.character-counter {
    text-align: right;
    font-size: 12px;
    color: #666;
    margin-top: 4px;
}

.modal-actions {
    padding: 12px 20px 16px;
    justify-content: flex-end;
}
</style>
